import { memo, useState, type KeyboardEvent, type ChangeEvent, useId } from 'react'
import styled from 'styled-components'
import { useTrackFilterUtilityClicked } from '@/features/shared/analytics/filters'
import { getKey, keys } from '@/features/shared/constants/keyCodes'
import { SEARCH_FILTER_TYPE } from '@/features/shared/constants/global'
import { DISPLAY_FORMAT } from '@/features/shared/constants/analytics'
import { RadioButton } from '@shipt/design-system-radio-group'
import { type InputKeyboardEvent } from '@/features/shared/utils/dataFetching/types'
import { type SearchCategory } from '@/features/shop/services/Search/types'
import { ChevronLeftIcon } from '@shipt/design-system-icons'
import { useOnChangeFilter } from '@/features/shop/components/ProductFilter/interactions/useOnChangeFilter'
import { ViewMoreButton } from '@/features/shop/components/ProductFilter/ViewMoreButton'
import { fadeIn } from '@/features/shared/styles/effects'
import { screenSizes } from '@shipt/design-system-themes'
import { TransparentButton } from '@/features/shared/elements/Buttons'
import { Body } from '@shipt/design-system-typography'
import { isCurrentCategoryActive } from '@/features/shop/services/Search/utils'
import {
  useSkeletonLoaderContext,
  SkeletonLoader,
} from '@/features/shared/components/SkeletonLoader'
import { useValidSearchParams } from '@/features/shared/hooks/search'
import { Box } from '@shipt/design-system-layouts'
import { isKeyboardUser } from '@/features/shared/utils/detectKeyboardUser'

type Props = {
  items: SearchCategory[]
  expandAllCategories?: true
}

export const CategoryFilterSection = memo(({ items, expandAllCategories }: Props) => {
  const searchParams = useValidSearchParams()
  const isLoading = useSkeletonLoaderContext()
  const [shouldShowMore, setShouldShowMore] = useState(false)
  const { query, category_id, promotion_category_id } = searchParams

  const currentCategory = items.find(
    (category) => category.id === Number(category_id || promotion_category_id),
  )
  const { onChangeFilter } = useOnChangeFilter()
  const trackFilterUtilityClicked = useTrackFilterUtilityClicked()

  const { parentName, parentId, id } = currentCategory ?? {}
  const previousCategoryName = parentId ? parentName : 'All categories'
  const hasActiveCategoryFilter =
    currentCategory && isCurrentCategoryActive(searchParams, currentCategory)

  const defaultShowLimit = 6

  const showLimit =
    // expand all categories when a category is active, with no search query (generally on browse pages)
    // or if caller wants to always show all categories
    (id && !query) || expandAllCategories
      ? items.length
      : items.filter((i) => i.parentId).length + defaultShowLimit

  let categoryIndex = items.findIndex((item) => item.id === currentCategory?.id)
  if (categoryIndex === -1 || isLoading) categoryIndex = 0

  const radioId = useId()

  if (!items.length) return null

  const categoriesShown = items.slice(categoryIndex, shouldShowMore ? items.length : showLimit)
  const nextCategoriesToShow = items.slice(categoryIndex + showLimit)

  const handleTrackFilterClicked = (categoryId: number, previousId?: number) => {
    const selectedCategory = items.find((item) => item.id === categoryId)
    const filterIdx = categoriesShown.findIndex((c) => c.id === categoryId)

    trackFilterUtilityClicked({
      params: { category_id: previousId || categoryId },
      lineage: previousId
        ? items.find((item) => item.id === previousId)?.lineage
        : selectedCategory?.lineage,
      filter_active: selectedCategory?.id !== currentCategory?.id,
      filter_value: selectedCategory?.name || '',
      // DS team wants us to send -1 if the user clicked on a parent category in order to
      // return to the previous category page
      list_index: parentId === previousId ? -1 : filterIdx,
      utility_type: 'filter',
      display_format: DISPLAY_FORMAT.VERTICAL,
      display_sublocation: `${SEARCH_FILTER_TYPE.CATEGORY}_${DISPLAY_FORMAT.VERTICAL}`,
    })
  }

  const handleCategorySearch = (selectedId: string) => {
    const categoryId = Number(selectedId)

    if (categoryId === currentCategory?.id && parentId) {
      return handleSelectPreviousCategory()
    }
    onChangeFilter({ category_id: categoryId })
    handleTrackFilterClicked(categoryId)
    setShouldShowMore(false)
  }

  // For pressing space bar on category radio button ONLY. Trying to be a11y-friendly when arrowing through radio buttons!
  const handleCategoryKeyPress = (e: InputKeyboardEvent) => {
    if (getKey(e) === keys.SPACE) {
      handleCategorySearch(e.currentTarget.value)
    }
  }

  const handleOnChangeFilter = (
    e: ChangeEvent<HTMLInputElement> | KeyboardEvent<HTMLInputElement>,
  ) => {
    const option = e.currentTarget.value
    handleCategorySearch(option)
  }

  function shouldIndentCategory(category: SearchCategory) {
    // expanded H2s for H1 when user is searching, not browsing
    if (!currentCategory?.id && Boolean(category.parentId)) {
      return true
    }
    return (
      category.id !== currentCategory?.id &&
      category.lineageDepth >= Number(currentCategory?.lineageDepth) &&
      category.lineageDepth > 0
    )
  }

  const handleShowMoreClick = () => setShouldShowMore(!shouldShowMore)

  const handleSelectPreviousCategory = () => {
    // do nothing if user clicks on the same category but there is no previous category
    if (hasActiveCategoryFilter) {
      // only track the filter if there is a parent category
      if (parentId && currentCategory.id) {
        handleTrackFilterClicked(currentCategory.id, parentId)
      }
      // do NOT send `category_id: 0` when "All Categories" is clicked
      onChangeFilter({ category_id: parentId || undefined })
    }
  }
  const previousCategoryFilter = hasActiveCategoryFilter ? (
    <PreviousCategoryButton
      onClick={handleSelectPreviousCategory}
      aria-label={`Select to go back to ${previousCategoryName}`}
    >
      <ChevronLeftIcon size="md" />
      <Body variant="secondary" as="span">
        {previousCategoryName}
      </Body>
    </PreviousCategoryButton>
  ) : null

  if (isLoading) {
    return (
      <>
        {previousCategoryFilter}
        {!!currentCategory && (
          <Box padding={{ base: ['xs', 0], md: ['sm', 0] }}>
            <CategoryRadioButton
              id={String(currentCategory.id)}
              name={currentCategory.name}
              checked
              readOnly
              key={currentCategory.id}
              label={currentCategory.name}
              value={String(currentCategory.id)}
              shouldIndent={shouldIndentCategory(currentCategory)}
              size="sm"
            />
          </Box>
        )}
        {categoriesShown.map((category) => {
          if (category.id === currentCategory?.id) return null
          if (category.id === currentCategory?.parentId) return null
          return <RadioSkeletonLoader key={category.id} isLoading />
        })}
      </>
    )
  }
  return (
    <>
      {previousCategoryFilter}
      {categoriesShown.map((category, i) => (
        <Box key={category.id} padding={{ base: ['xs', 0], md: ['sm', 0] }}>
          <CategoryRadioButton
            name={`category filters ${radioId}`}
            key={category.id}
            shouldFadeIn={i >= showLimit}
            shouldIndent={shouldIndentCategory(category)}
            label={category.name}
            value={String(category.id)}
            onKeyDown={handleCategoryKeyPress}
            checked={currentCategory?.id === category.id}
            onChange={(e) => !isKeyboardUser() && handleOnChangeFilter(e)}
            size="sm"
          />
        </Box>
      ))}
      {nextCategoriesToShow.length > 0 && (
        <ViewMoreButton isShowingMore={shouldShowMore} onClick={handleShowMoreClick} />
      )}
    </>
  )
})

const RadioSkeletonLoader = styled(SkeletonLoader)`
  height: 32px;
  width: 300px;
  margin: 0.5rem 0;

  @media ${screenSizes.tablet} {
    height: 1.5rem;
    width: 12.5rem;
    margin: 0.375rem 0;
  }
`

interface RadioButtonProps {
  shouldIndent: boolean
  shouldFadeIn?: boolean
}

const CategoryRadioButton = styled(RadioButton)<RadioButtonProps>`
  animation: ${({ shouldFadeIn }) => shouldFadeIn && fadeIn};
  padding-left: ${({ shouldIndent }) => (shouldIndent ? 32 : 0)}px;

  @media ${screenSizes.tablet} {
    padding-left: ${({ shouldIndent }) => (shouldIndent ? 24 : 0)}px;
  }
`

const PreviousCategoryButton = styled(TransparentButton)`
  display: flex;
  align-items: center;
  padding: 8px 8px 8px 0;
  color: ${({ theme }) => theme.gray600};

  &:hover {
    color: ${({ theme }) => theme.purple500};
  }

  @media ${screenSizes.tablet} {
    padding: 0 8px 4px 0;
  }

  svg {
    margin-right: 0.5rem;

    @media ${screenSizes.tablet} {
      margin-right: 0.25rem;
      margin-left: -0.25rem;
    }
  }
`
